import React, { FC, useState, useRef, useEffect } from 'react';
import { 
  Sparkles, 
  Send, 
  TrendingUp, 
  Bookmark, 
  HelpCircle, 
  ArrowRight,
  ShieldAlert,
  Zap,
  RefreshCw,
  Clock
} from 'lucide-react';

interface ChatMessage {
  id: string;
  sender: 'user' | 'ai';
  text: string;
  timestamp: Date;
}

export const AiCopilotView: FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome-msg',
      sender: 'ai',
      text: 'Xoş gördük, QR Menu SaaS platformasının Baş Admini! Mən sizin CRM operativ verilənlər bazasına canlı qoşulmuş süni intellekt analitikinizəm.\n\nSistemdəki abunəlik tarifləri, açıq dəstək sorğuları, aylıq QR skan limitləri və müştəri itkisi riski barədə bütün sualları cavablandırmağa hazıram. Suallarınızı daxil edin və ya aşağıdakı hazır ssenarilərdən birini seçin! 👇',
      timestamp: new Date()
    }
  ]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatBottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSendPrompt = async (textToSend: string) => {
    if (!textToSend.trim() || isLoading) return;

    // Append user query bubble
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: textToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    setUserInput('');
    setIsLoading(true);

    try {
      const res = await fetch('/api/ai/co-pilot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: textToSend }),
      });
      const data = await res.json();
      
      const aiMsg: ChatMessage = {
        id: `ai-${Date.now()}`,
        sender: 'ai',
        text: data.response || 'Sorğu işlənərkən xəta baş verdi. Zəhmət olmasa yenidən cəhd edin.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMsg]);
    } catch (err) {
      console.error(err);
      const errorMsg: ChatMessage = {
        id: `err-${Date.now()}`,
        sender: 'ai',
        text: 'Süni İntellekt serveri ilə sınaq mərkəzində əlaqə qurmaq mümkün olmadı. Zəhmət olmasa Settings > Secrets bölməsində GEMINI_API_KEY təyin edildiyini yoxlayın.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  // Quick Prompt Recommendations for Admin
  const starters = [
    {
      label: 'Müştəri İtkisi (Churn Riski)',
      prompt: 'Bütün restoranlar üzrə statuslar, skan statistikaları və sınaq bitmə tarixlərinə əsasən müfəssəl itki (churn risk) analizi apar. Yüksək risk daşıyan restoranları qeyd et.',
      icon: ShieldAlert,
      color: 'bg-rose-50 text-rose-750 border-rose-200 hover:border-rose-400'
    },
    {
      label: 'Abunəliyə Keçid Kampaniyası',
      prompt: 'Müştərilərin ödənişsiz sınaq (Free) abunəliyindən ödənişli planlara keçməsini təşviq etmək üçün addım-addım aksiya təklif et və tərəfdaşlar üçün rəsmi bildiriş mətni qur.',
      icon: Zap,
      color: 'bg-amber-50 text-amber-705 border-amber-205 hover:border-amber-400'
    },
    {
      label: 'Dəstək Biletlərinin Auditi',
      prompt: 'Sistemdəki aktiv açıq dəstək müraciətlərini analiz et. Partnyorların qarşılaşdıqları ən geniş yayılmış texniki problemlər hansılardır və bunları gələcəkdə necə səmərəli şəkildə həll edə bilərik?',
      icon: TrendingUp,
      color: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:border-indigo-400'
    }
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      
      {/* Title Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-display font-medium text-2xl tracking-tight text-slate-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600 animate-pulse" />
            <span>Executive Business AI Co-pilot</span>
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Sistemdəki bütün metrikləri real zaman rejimində dərindən analiz edən professional İİ biznes-köməkçisi.
          </p>
        </div>
        <div className="flex bg-slate-100 px-3 py-1.5 rounded-full text-xs font-mono font-bold text-indigo-700 gap-1.5 items-center border border-slate-200">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span>Gemini 3.5 Active context</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* Left column: Starters prompts and quick guides */}
        <div className="lg:col-span-4 space-y-5">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <h3 className="font-display font-bold text-sm text-slate-800 uppercase tracking-widest font-mono">
              Sürətli İİ Analiz Ssenariləri
            </h3>
            <p className="text-xs text-slate-400">
              Süni intellektin dərhal dərin hesablamalar aparmasını təmin etmək üçün hazır şablonlardan yararlanın:
            </p>

            <div className="space-y-3">
              {starters.map((s, idx) => {
                const Icon = s.icon;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSendPrompt(s.prompt)}
                    className={`w-full text-left p-3.5 rounded-xl border transition-all duration-200 flex gap-3 cursor-pointer ${s.color}`}
                  >
                    <Icon className="w-5 h-5 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold font-sans leading-snug">{s.label}</p>
                      <p className="text-[10px] text-slate-500 line-clamp-2 mt-1 leading-relaxed">{s.prompt}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick tips box */}
          <div className="bg-slate-900 text-slate-300 p-5 rounded-2xl border border-slate-800 space-y-3.5 relative overflow-hidden">
            <div className="absolute right-0 bottom-0 w-24 h-24 bg-indigo-600/10 rounded-full blur-xl pointer-events-none" />
            <h4 className="font-display font-extrabold text-xs text-indigo-400 tracking-wider font-mono">
              Süni İntellekt İnteqrasiya Qaydası
            </h4>
            <ul className="text-[11px] space-y-2 leading-relaxed font-sans text-slate-400 list-disc list-inside">
              <li>Süni intellekt ümumi müəssisə sayını, MRR balını, reytinqi və skan limitlərini nəzarətə götürür.</li>
              <li>Ssenarilər vasitəsilə diqqət tələb edən restoranları cədvəl halında strukturlaya bilərsiniz.</li>
              <li>Model tərəfindən professional bildiriş mətnləri avtomatik olaraq formalaşdırıla bilir.</li>
            </ul>
          </div>
        </div>

        {/* Right column: Interactive Chat Canvas */}
        <div className="lg:col-span-8 bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm flex flex-col h-[580px] justify-between">
          
          {/* Chat Header */}
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold">
                <Sparkles className="w-4 h-4 text-indigo-550 animate-pulse" />
              </div>
              <div>
                <h3 className="font-display font-extrabold text-sm text-slate-900">Süni İntellekt Konsolu</h3>
                <p className="text-[10px] text-slate-400 font-mono tracking-wide">Connected via WebSocket Proxy · Server CJS</p>
              </div>
            </div>
            
            <div className="flex items-center gap-1.5 text-xs text-slate-400 font-mono">
              <Clock className="w-3.5 h-3.5 text-slate-400" />
              <span>Context: 128k</span>
            </div>
          </div>

          {/* Messages Flow Canvas */}
          <div className="flex-1 p-6 overflow-y-auto space-y-5 bg-slate-50/20">
            {messages.map((msg) => {
              const isAi = msg.sender === 'ai';
              return (
                <div 
                  key={msg.id}
                  className={`flex gap-3 max-w-[85%] ${isAi ? 'mr-auto' : 'ml-auto flex-row-reverse'}`}
                >
                  <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-xs font-mono font-bold ${
                    isAi ? 'bg-indigo-600 text-white shadow-sm' : 'bg-slate-300 text-slate-700'
                  }`}>
                    {isAi ? 'AI' : 'AY'}
                  </div>

                  <div className="space-y-1">
                    <div className={`p-4 rounded-xl text-xs font-sans leading-relaxed shadow-sm border whitespace-pre-wrap ${
                      isAi 
                        ? 'bg-white text-slate-800 rounded-tl-none border-slate-100' 
                        : 'bg-indigo-600 text-white rounded-tr-none border-indigo-650'
                    }`}>
                      {msg.text}
                    </div>

                    <p className={`text-[9px] font-mono text-slate-400 ${isAi ? 'text-left' : 'text-right'}`}>
                      {msg.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              );
            })}

            {/* AI Generation Loader State bubble */}
            {isLoading && (
              <div className="flex gap-3 max-w-[80%] mr-auto animate-fade-in">
                <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-mono font-bold animate-pulse">
                  AI
                </div>
                <div className="bg-white p-4 rounded-2xl border border-slate-100 text-xs text-slate-500 rounded-tl-none shadow-xs flex items-center gap-2 font-mono">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-650" />
                  <span>Analitik göstəricilər hesablanır...</span>
                </div>
              </div>
            )}

            <div ref={chatBottomRef} />
          </div>

          {/* Submit input box */}
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              handleSendPrompt(userInput);
            }}
            className="p-4 bg-slate-50 border-t border-slate-200 flex items-center gap-3 shrink-0"
          >
            <input
              id="ai-copilot-input"
              type="text"
              required
              disabled={isLoading}
              placeholder="Süni İntellektdən risklər, marketinq, abunəliklər barədə soruşun və ya istədiyiniz sualı yazın..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              className="flex-1 bg-white border border-slate-200 py-2.5 px-4 text-xs rounded-xl focus:outline-hidden focus:border-indigo-500 disabled:bg-slate-100 placeholder-slate-400"
            />
            
            <button
              id="btn-send-copilot"
              type="submit"
              disabled={isLoading || !userInput.trim()}
              className="p-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white rounded-xl transition-all font-bold cursor-pointer"
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </form>

        </div>

      </div>
    </div>
  );
};
